import { computed } from 'vue'

import { resolveProviderUuid } from '@/composables/useProviderAccess'
import ability from '@/plugins/casl/ability'
import { useUserStore } from '@/stores/user'
import type { VerticalNavItems } from '@layouts/types'

export function useProviderNavItems(): { navItems: ReturnType<typeof computed<VerticalNavItems>> } {
  const providerUuid = computed(() => resolveProviderUuid() ?? '')

  const navItems = computed<VerticalNavItems>(() => {
    // No resolvable provider context (e.g. the one reactive tick between a
    // logout clearing the route/active-provider and this layout unmounting)
    // — every item below requires a non-empty `provider_uuid` route param,
    // so building them here would throw inside Vue Router's path matcher
    // when the sidebar's router-links try to resolve an href (Rollbar #1303).
    if (!providerUuid.value)
      return []

    const items: VerticalNavItems = []

    // VH-3574: only for the users the hospital chose to work the inbox (everyone, until it picks).
    // Added conditionally, like the Reporting items below: VerticalNavLink ignores action/subject.
    if (ability.can('manage', 'InsuranceInbox')) {
      items.push({
        // VH-2583: first, because it's the landing surface the nightly discovery digest links
        // into — the reason most people arrive in IVD each morning.
        //
        // No badgeContent yet. An open-item count is wanted (it's what gives staff an ambient
        // sense of backlog instead of relying on a daily email), but it needs a refresh story
        // first: fetching it here would fire /insurance/inbox for every provider user this nav
        // renders for, and populating it only after someone opens the page defeats the point.
        title: 'Inbox',
        icon: { icon: 'mdi-inbox-outline' },
        action: 'manage',
        subject: 'InsuranceInbox',
        to: { name: 'iv-inbox', params: { provider_uuid: providerUuid.value } },
      })
    }

    // VH-2263: Look Up Insurance replaces "Verify" as the primary entry point for both verify and
    // discover flows. The old separate mode-select page (iv-verify) is gone entirely now — VH-2263
    // collapsed it into iv-lookup, which LookupFlow.vue serves.
    // Only with IVD (VH-3637): at a hospital without it these links bounce to "account isn't set up".
    if (ability.can('read', 'VerificationSession')) {
      items.push(
        {
          title: 'Look Up Insurance',
          icon: { icon: 'mdi-account-search-outline' },
          action: 'read',
          subject: 'VerificationSession',
          to: { name: 'iv-lookup', params: { provider_uuid: providerUuid.value } },
        },
        {
          title: 'Bulk Discover & Verify',
          icon: { icon: 'mdi-magnify-expand' },
          action: 'read',
          subject: 'VerificationSession',
          to: { name: 'iv-bulk', params: { provider_uuid: providerUuid.value } },
        },
        {
          title: 'Price List',
          icon: { icon: 'mdi-receipt-text-outline' },
          action: 'read',
          subject: 'VerificationSession',
          to: { name: 'iv-price-list', params: { provider_uuid: providerUuid.value } },
        },
        {
          title: 'History',
          icon: { icon: 'mdi-history' },
          action: 'read',
          subject: 'VerificationSession',
          to: { name: 'iv-history', params: { provider_uuid: providerUuid.value } },
        },
      )
    }

    // Reporting group, provider_admin only (VH-3450). Items are added only when the user has the
    // ability: VerticalNavLink ignores action/subject on a link (only the heading checks them), so
    // an item left in the list would show to staff. ability.can() is reactive here (@casl/vue wraps
    // it), so this recomputes when /profile loads the user's abilities.
    if (ability.can('read', 'ProviderReport')) {
      items.push({ heading: 'Reporting', action: 'read', subject: 'ProviderReport' })

      // VH-3273: Vector admins only for now, like the route, while the figures are checked against
      // real hospitals. When it opens to hospital admins, the group's `read ProviderReport` gate
      // above already covers them, so this inner check can go.
      if (useUserStore().data?.access?.vector === true) {
        items.push({
          title: 'Account Value',
          icon: { icon: 'mdi-chart-box-outline' },
          action: 'manage',
          subject: 'User',
          to: { name: 'provider-value-dashboard', params: { provider_uuid: providerUuid.value } },
        })
      }

      // VH-3551: released to hospital admins after VH-3450's production QA. The group's
      // `read ProviderReport` gate above is the only check it needs.
      items.push({
        title: 'Reports & Invoices',
        icon: { icon: 'mdi-file-document-multiple-outline' },
        action: 'read',
        subject: 'ProviderReport',
        to: { name: 'provider-reports', params: { provider_uuid: providerUuid.value } },
      })

      // Moved here from the top-right user menu. Same route, page and ability as before.
      if (ability.can('manage', 'User')) {
        items.push({
          title: 'Settings',
          icon: { icon: 'mdi-cog-outline' },
          action: 'manage',
          subject: 'User',
          to: { name: 'provider-settings', params: { provider_uuid: providerUuid.value } },
        })
      }
    }

    // SSDI is deliberately omitted here regardless of entitlement: plenty of production
    // hospitals already have ssdi_eligibility enabled, but there's no provider-facing SSDI
    // page built yet — the only route available (vector-ssdi-call-list) is an internal-admin
    // route with no `layout: 'provider'` meta, so navigating to it swaps the whole app shell
    // out from under the provider into the internal admin layout. Re-add this item once a
    // real provider-scoped SSDI page/route exists.

    return items
  })

  return { navItems }
}
